import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Loader2 } from 'lucide-react';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { supabase } from '../lib/supabase';

interface Mini3DViewerProps {
  glbUrl: string;
  assetId: string;
  resolvedGlbUrl?: string;
  height?: number;
  width?: number;
  clickable?: boolean;
  signedGlbAssetId?: string;
  allowDeletedSignedPreview?: boolean;
  fitParent?: boolean;
}

const MAX_CONCURRENT_MODEL_LOADS = 3;
let activeModelLoads = 0;
const modelLoadQueue: Array<() => void> = [];

const withModelLoadSlot = async <T,>(task: () => Promise<T>): Promise<T> => {
  if (activeModelLoads >= MAX_CONCURRENT_MODEL_LOADS) {
    await new Promise<void>((resolve) => {
      modelLoadQueue.push(resolve);
    });
  }

  activeModelLoads += 1;
  try {
    return await task();
  } finally {
    activeModelLoads = Math.max(0, activeModelLoads - 1);
    const next = modelLoadQueue.shift();
    if (next) next();
  }
};

const isDev = import.meta.env.DEV;
const logDebug = (..._args: unknown[]) => {};
const logWarn = (..._args: unknown[]) => {};
const logError = (..._args: unknown[]) => {};

const RESOLVE_CACHE_TTL_MS = 5 * 60 * 1000;
const resolvedUrlCache = new Map<string, { url: string; expiresAt: number }>();

const parseGltfFromArrayBuffer = (
  loader: GLTFLoader,
  buffer: ArrayBuffer
): Promise<{ scene: THREE.Group; animations?: THREE.AnimationClip[] }> =>
  new Promise((resolve, reject) => {
    loader.parse(
      buffer,
      '',
      (gltf) => resolve(gltf as { scene: THREE.Group; animations?: THREE.AnimationClip[] }),
      (error) => reject(error)
    );
  });

export default function Mini3DViewer({
  glbUrl,
  assetId,
  resolvedGlbUrl,
  height = 250,
  width = 250,
  clickable = true,
  signedGlbAssetId,
  allowDeletedSignedPreview = false,
  fitParent = false,
}: Mini3DViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRootRef = useRef<HTMLDivElement>(null);
  const wasDraggedRef = useRef(false);
  const fallbackUrl = `${import.meta.env.BASE_URL || '/'}assets/fallback_cube.glb`;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsInViewport(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const getLegacyLocalFallback = (value: string) => {
    let objectKey: string | undefined;
    if (value.startsWith('preview_assets/')) {
      objectKey = value.replace(/^preview_assets\//, '');
    } else {
      const match = value.match(/\/storage\/v1\/object\/public\/preview_assets\/(.+?)(\?|$)/i);
      if (match?.[1]) {
        objectKey = decodeURIComponent(match[1]);
      }
    }

    if (!objectKey) return undefined;
    const localKey = objectKey.replace(/model_low\.glb$/i, 'model.glb');
    return `${import.meta.env.BASE_URL || '/'}assets/${localKey}`;
  };

  const createProceduralFallback = () => {
    const fallbackGroup = new THREE.Group();
    const cubeGeometry = new THREE.BoxGeometry(1.5, 1.5, 1.5);
    const cubeMaterial = new THREE.MeshStandardMaterial({
      color: 0xf97316,
      roughness: 0.35,
      metalness: 0.2
    });
    const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
    cube.castShadow = cube.receiveShadow = true;
    cube.rotation.set(-0.3, 0.5, 0);
    fallbackGroup.add(cube);

    const platformGeometry = new THREE.CylinderGeometry(1.8, 1.8, 0.25, 32);
    const platformMaterial = new THREE.MeshStandardMaterial({
      color: 0x111827,
      roughness: 0.7,
      metalness: 0.1
    });
    const platform = new THREE.Mesh(platformGeometry, platformMaterial);
    platform.position.y = -0.85;
    fallbackGroup.add(platform);

    return fallbackGroup;
  };

  useEffect(() => {
    if (!isInViewport) return;
    if (!containerRef.current && !rendererRootRef.current) return;

    let renderer: THREE.WebGLRenderer | null = null;
    let scene: THREE.Scene | null = null;
    let camera: THREE.PerspectiveCamera | null = null;
    let model: THREE.Group | null = null;
    let mixer: THREE.AnimationMixer | null = null;
    let controls: OrbitControls | null = null;
    let animationId: number | null = null;
    let disposed = false;
    let isUserInteracting = false;
    let handleResize: (() => void) | null = null;

    const init = async () => {
      try {
        logDebug('[Mini3DViewer] Initializing viewer for asset:', assetId);
        logDebug('[Mini3DViewer] Initial glbUrl:', glbUrl);
        
        scene = new THREE.Scene();
        scene.background = null;

        const initialWidth = fitParent
          ? Math.max(1, (containerRef.current?.clientWidth ?? 1))
          : width;
        const initialHeight = fitParent
          ? Math.max(1, (containerRef.current?.clientHeight ?? 1))
          : height;

        camera = new THREE.PerspectiveCamera(45, initialWidth / initialHeight, 0.1, 1000);
        camera.position.set(0, 1, 5);

        renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          powerPreference: 'low-power',
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 0.75));
        renderer.setSize(initialWidth, initialHeight);
        renderer.setClearColor(0x000000, 0);
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;

        // Append renderer into a dedicated inner root to avoid interfering with React children
        (rendererRootRef.current ?? containerRef.current)?.appendChild(renderer.domElement);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
        scene.add(ambientLight);

        const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.0);
        directionalLight1.position.set(5, 5, 5);
        scene.add(directionalLight1);

        const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
        directionalLight2.position.set(-5, 3, -5);
        scene.add(directionalLight2);

        controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enableZoom = true;
        controls.enablePan = true;
        controls.minDistance = 2;
        controls.maxDistance = 10;
        controls.maxPolarAngle = Math.PI;
        controls.minPolarAngle = 0;
        controls.autoRotate = true;
        controls.autoRotateSpeed = 1.0;

        controls.addEventListener('start', () => {
          isUserInteracting = true;
          wasDraggedRef.current = false;
          if (controls) controls.autoRotate = false;
        });

        controls.addEventListener('change', () => {
          // If controls have changed position, they were dragged
          wasDraggedRef.current = true;
        });

        controls.addEventListener('end', () => {
          isUserInteracting = false;
          setTimeout(() => {
            if (!isUserInteracting && controls) {
              controls.autoRotate = true;
            }
          }, 2000);
        });

        handleResize = () => {
          if (!renderer || !camera) return;
          const nextWidth = fitParent
            ? Math.max(1, (containerRef.current?.clientWidth ?? 1))
            : width;
          const nextHeight = fitParent
            ? Math.max(1, (containerRef.current?.clientHeight ?? 1))
            : height;
          camera.aspect = nextWidth / nextHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(nextWidth, nextHeight);
        };
        window.addEventListener('resize', handleResize);

        let resolvedUrl: string | undefined =
          typeof resolvedGlbUrl === 'string' && resolvedGlbUrl.trim().length > 0
            ? resolvedGlbUrl
            : undefined;
        const cacheKey = `${assetId}|${glbUrl}`;
        const cacheHit = resolvedUrlCache.get(cacheKey);
        if (!resolvedUrl && cacheHit && cacheHit.expiresAt > Date.now()) {
          resolvedUrl = cacheHit.url;
        }

        if (!resolvedUrl && assetId) {
          try {
            const { data, error: resolveError } = await supabase.functions.invoke('assets-preview-resolve', {
              body: {
                assetId,
                hintPath: glbUrl,
                includeDeleted: allowDeletedSignedPreview,
              },
            });
            const resolvedFromApi = (data as any)?.url;
            if (!resolveError && typeof resolvedFromApi === 'string') {
              resolvedUrl = resolvedFromApi;
              const ttlSeconds = Number((data as any)?.cacheTtlSeconds) || RESOLVE_CACHE_TTL_MS / 1000;
              resolvedUrlCache.set(cacheKey, {
                url: resolvedFromApi,
                expiresAt: Date.now() + Math.max(30, ttlSeconds) * 1000,
              });
            } else if (resolveError) {
              logWarn('Mini3DViewer: preview resolver failed:', resolveError.message);
            }
          } catch (resolveErr) {
            logWarn('Mini3DViewer: preview resolver exception:', resolveErr);
          }
        }

        if (signedGlbAssetId && glbUrl.trim().length > 0) {
          const { data, error: downloadError } = await supabase.functions.invoke('assets-download', {
            body: {
              assetId: signedGlbAssetId,
              format: 'glb',
              includeDeleted: allowDeletedSignedPreview,
            },
          });
          const url = (data as any)?.downloadUrl || (data as any)?.signedUrl || (data as any)?.url;
          if (!downloadError && typeof url === 'string' && url.length > 8) {
            resolvedUrl = url;
          } else if (downloadError) {
            logWarn('Mini3DViewer: Failed to authorize full-res preview:', downloadError.message);
          }
        }

        if (!resolvedUrl) {
          resolvedUrl = await resolveAssetUrl(glbUrl);
        }

        if (!resolvedUrl) {
          logWarn('Mini3DViewer: Failed to resolve GLB URL, using fallback cube');
          logWarn('Original glbUrl:', glbUrl);
          logWarn('Asset ID:', assetId);
          resolvedUrl = fallbackUrl;
        }

        if (disposed) return;

        const loader = new GLTFLoader();
        let gltf: { scene: THREE.Group; animations?: THREE.AnimationClip[] } | null = null;

        const loadWithTimeout = (url: string, timeoutMs: number = 30000) => {
          const loadViaFetchAndParse = async () => {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Model request failed (${response.status})`);
            }
            const arrayBuffer = await response.arrayBuffer();
            return parseGltfFromArrayBuffer(loader, arrayBuffer);
          };

          return withModelLoadSlot(() =>
            Promise.race([
              loadViaFetchAndParse(),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Model load timeout')), timeoutMs)
              ),
            ])
          );
        };

        try {
          logDebug('Mini3DViewer: Loading model from:', resolvedUrl);
          gltf = await loadWithTimeout(resolvedUrl);
        } catch (loadError: any) {
          logError('Mini3DViewer: Failed to load 3D model from:', resolvedUrl);
          logError('Original glbUrl:', glbUrl);
          logError('Asset ID:', assetId);
          logError('Error details:', loadError?.message || loadError);

          const isRemoteResolvedUrl = /^https?:\/\//i.test(resolvedUrl);
          if (isDev || !isRemoteResolvedUrl) {
            const legacyFallback = getLegacyLocalFallback(resolvedUrl);
            if (legacyFallback) {
              logDebug('Mini3DViewer: Attempting legacy local fallback:', legacyFallback);
              try {
                gltf = await loadWithTimeout(legacyFallback);
                logDebug('Mini3DViewer: Legacy local fallback loaded successfully');
              } catch (legacyError) {
                logError('Mini3DViewer: Legacy local fallback failed:', legacyError);
              }
            }
          }

          if (!gltf && resolvedUrl !== fallbackUrl) {
            logDebug('Mini3DViewer: Attempting to load fallback cube...');
            try {
              gltf = await loadWithTimeout(fallbackUrl);
              logDebug('Mini3DViewer: Fallback cube loaded successfully');
            } catch (fallbackError) {
              logError('Mini3DViewer: Even fallback cube failed to load:', fallbackError);
            }
          }

          if (!gltf) {
            logWarn('Mini3DViewer: Falling back to procedural mesh after network fallbacks failed');
            const procedural = createProceduralFallback();
            if (procedural) {
              gltf = {
                scene: procedural,
                animations: [],
              };
            } else {
              if (!disposed) {
                setError(true);
                setLoading(false);
              }
              return;
            }
          }
        }

        if (disposed) return;
        if (!gltf) return;

        model = gltf.scene;

        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2.5 / maxDim;
        model.scale.multiplyScalar(scale);

        box.setFromObject(model);
        box.getCenter(center);
        model.position.sub(center);

        scene.add(model);

        if (gltf.animations && gltf.animations.length > 0) {
          mixer = new THREE.AnimationMixer(model);
          const action = mixer.clipAction(gltf.animations[0]);
          action.play();
        }

        setLoading(false);

        const clock = new THREE.Clock();

        const animate = () => {
          if (disposed) return;

          animationId = requestAnimationFrame(animate);

          const delta = clock.getDelta();

          if (controls) {
            controls.update();
          }

          if (mixer) {
            mixer.update(delta);
          }

          if (renderer && scene && camera) {
            renderer.render(scene, camera);
          }
        };

        animate();
      } catch (err) {
        logError('Failed to load 3D model:', err);
        if (!disposed) {
          setError(true);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      disposed = true;

      if (animationId !== null) {
        cancelAnimationFrame(animationId);
      }

      if (handleResize) {
        window.removeEventListener('resize', handleResize);
        handleResize = null;
      }

      if (controls) {
        controls.dispose();
        controls = null;
      }

      if (mixer) {
        mixer.stopAllAction();
        mixer = null;
      }

      if (model) {
        model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (child.geometry) {
              child.geometry.dispose();
            }
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach((mat) => mat.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
        scene?.remove(model);
        model = null;
      }

      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
        const rootEl = rendererRootRef.current ?? containerRef.current;
        if (rootEl && renderer.domElement.parentElement === rootEl) {
          rootEl.removeChild(renderer.domElement);
        }
        renderer = null;
      }

      if (scene) {
        scene.clear();
        scene = null;
      }

      camera = null;
    };
  }, [glbUrl, resolvedGlbUrl, width, height, signedGlbAssetId, allowDeletedSignedPreview, fitParent, isInViewport]);

  const handleClick = () => {
    // Only navigate if the user wasn't dragging the 3D model
    if (clickable && !wasDraggedRef.current) {
      navigate(`/viewer?id=${assetId}`);
    }
    // Reset the drag flag after handling click
    wasDraggedRef.current = false;
  };

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      className={`relative overflow-hidden ${
        fitParent ? 'w-full h-full' : 'rounded-xl bg-white shadow-inner border border-slate-200'
      } ${
        clickable ? 'cursor-pointer hover:shadow-lg hover:border-orange-300 transition-all' : ''
      }`}
      style={
        fitParent
          ? undefined
          : { width: `${width}px`, height: `${height}px` }
      }
    >
      <div ref={rendererRootRef} className="w-full h-full" />

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm bg-slate-50">
          Failed to load
        </div>
      )}
    </div>
  );
}
